import { setUserId } from '@snowplow/browser-tracker';
import { NotificationProvider, DialogsProvider } from '@wheely/ui-kit';
import { AuthProvider } from '@wheely/web-auth-lib/legacy';
import { AuthProvider as V3AuthProvider } from '@wheely/web-auth-lib/react';
import { CurrentPageSpanStoreManager } from '@wheely/web-tracing';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { Router } from 'react-router-dom';

import { PermissionsProvider } from './app/contexts/permissions';
import { App } from './app/view/App';
import { Tracer } from './app/view/Tracer';
import { IntlProvider } from './i18n';
import { authAPI } from './modules/auth/api';
import { authClientV3 } from './modules/auth/api/v3';
import { bootstrapSession } from './modules/auth/bootstrapSession';
import { authJournal, journalAuthClient } from './modules/auth/journal';
import { getAuthMode } from './modules/auth/mode';
import { rootStore } from './root-store';
import { reset } from './rootActions';
import { errorHandling } from './services/errorHandling';
import { eventTracking } from './services/event-tracking';
import { history } from './services/history';
import { getApiUrl } from './shared/utils/getApiUrl';

eventTracking.init();

// Legacy: until 2023 auth ran through a service worker.
// SW registrations never expire — a visitor from that era still carries one,
// and a stale worker intercepts requests before the new app loads.
// Unregistering is free; keep this while such visitors are possible.
if (window.navigator && navigator.serviceWorker) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

CurrentPageSpanStoreManager.configure({
  tracesEndpoint: '/api/traces',
  tracesResourceName: 'front-chauffeur-onboarding',
  tracesCorsUrls: [getApiUrl()],
  tracesConsoleEnabled: false,
  tracesResourceVersion: '0.0.3-alpha',
  tracesInstumentationUse: {
    fetch: true,
    documentLoad: true,
    xhr: true,
  },
});

const renderApp = async () => {
  const node = document.querySelector('.react-app-root');

  if (node === null) {
    throw new Error(`onboarding wizard fail: can't find element .react-app-root`);
  }

  // In the V3 stack a forced sign-out arrives as an event, not as an HTTP
  // error chain: reset the app the same way the sign-out thunk does.
  authClientV3.on('signedOut', () => {
    rootStore.dispatch(reset());
    errorHandling.clearIdentity();
    setUserId(null);
    history.replace('/login');
  });

  journalAuthClient(authClientV3);

  const bootstrapStartedAt = performance.now();
  const { legacyIsAuthenticated } = await bootstrapSession(authClientV3);

  authJournal.step('bootstrapped', {
    status: authClientV3.getState().status,
    auth_mode: getAuthMode(),
    legacy_authenticated: legacyIsAuthenticated,
    took_ms: Math.round(performance.now() - bootstrapStartedAt),
  });

  createRoot(node).render(
    <React.StrictMode>
      <V3AuthProvider client={authClientV3}>
        <AuthProvider initialIsAuthenticated={legacyIsAuthenticated} authApi={authAPI}>
          <ReduxProvider store={rootStore}>
            <IntlProvider>
              <Router history={history}>
                <Tracer />
                <DialogsProvider>
                  <NotificationProvider>
                    <PermissionsProvider>
                      <App />
                    </PermissionsProvider>
                  </NotificationProvider>
                </DialogsProvider>
              </Router>
            </IntlProvider>
          </ReduxProvider>
        </AuthProvider>
      </V3AuthProvider>
    </React.StrictMode>,
  );
};

export { renderApp };
